import React from 'react';
import { Space, Table, Typography } from 'antd';
import { DashboardPrimaryActionButton, DashboardRefreshButton, DashboardSearchInput } from '../dashboard';
import { MaintenanceStatusTag, ServiceStatusTag } from './MaintenanceStatusTags';

function valueOrBlank(value) {
  return value || value === 0 ? value : '';
}

function joinParts(parts) {
  return parts.map(valueOrBlank).filter(Boolean).join(' ');
}

function ForkliftMobileCard({ forklift, selected, onSelect, onViewRecords }) {
  const makeModel = joinParts([forklift.make, forklift.model]);
  const serial = valueOrBlank(forklift.serialNumber);
  const hours = valueOrBlank(forklift.hourMeter);
  const metadata = [serial ? `Serial ${serial}` : '', hours || hours === 0 ? `${hours} hrs` : '']
    .filter(Boolean)
    .join(' · ');

  return (
    <article
      className={`maintenance-forklift-mobile-card shared-selected-card${selected ? ' is-selected' : ''}`}
      onClick={() => onSelect(forklift)}
    >
      <div className="maintenance-forklift-mobile-card-header">
        <span className="maintenance-forklift-mobile-unit">{valueOrBlank(forklift.unitId) || 'Unit unavailable'}</span>
        <span className="maintenance-forklift-mobile-badges">
          <ServiceStatusTag status={forklift.status} />
          <MaintenanceStatusTag status={forklift.maintenanceStatus} />
        </span>
      </div>
      <div className="maintenance-forklift-mobile-details">
        <div className="maintenance-forklift-mobile-model">{makeModel}</div>
        <div className="maintenance-forklift-mobile-meta">{metadata}</div>
      </div>
      <div className="forklifts-page-mobile-actions customer-forklifts-mobile-actions maintenance-forklift-mobile-actions">
        <DashboardPrimaryActionButton onClick={(e) => { e.stopPropagation(); onViewRecords(forklift); }}>View Records</DashboardPrimaryActionButton>
      </div>
    </article>
  );
}

export function MaintenanceForkliftList({
  helperText,
  forklifts,
  selectedForkliftId,
  rowClassName,
  expandedRowRender,
  onSelectForklift,
  onViewRecords,
  search,
  onSearchChange,
  onRefresh,
  desktopRecordsPanel,
}) {
  const columns = [
    { title: 'Unit ID', dataIndex: 'unitId', key: 'unitId', render: valueOrBlank },
    { title: 'Make', dataIndex: 'make', key: 'make' },
    { title: 'Model', dataIndex: 'model', key: 'model' },
    { title: 'Serial #', dataIndex: 'serialNumber', key: 'serialNumber' },
    { title: 'Hours', dataIndex: 'hourMeter', key: 'hourMeter', render: valueOrBlank },
    { title: 'Service', dataIndex: 'status', key: 'status', render: (value) => <ServiceStatusTag status={value} /> },
    { title: 'Maintenance', dataIndex: 'maintenanceStatus', key: 'maintenanceStatus', render: (value) => <MaintenanceStatusTag status={value} /> },
  ];

  return (
    <section className={`dashboard-fleet-table-area maintenance-list-area${desktopRecordsPanel ? ' maintenance-list-area-records-open' : ''}`}>
      <Space
        className={`dashboard-fleet-table-toolbar maintenance-list-toolbar${
          helperText ? '' : ' maintenance-list-toolbar-no-helper'
        }`}
        align="center"
        wrap
      >
        {helperText ? (
          <div className="dashboard-fleet-table-toolbar-copy">
            <Typography.Text type="secondary" className="dashboard-fleet-helper-text">
              {helperText}
            </Typography.Text>
          </div>
        ) : null}
        <Space className="dashboard-fleet-table-controls" wrap>
          <DashboardSearchInput
            ariaLabel="Search forklifts"
            placeholder="Search forklifts"
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            className="dashboard-fleet-search maintenance-list-search"
          />
          <DashboardRefreshButton onClick={onRefresh} className="maintenance-list-refresh">
            Refresh
          </DashboardRefreshButton>
        </Space>
      </Space>

      <div className="maintenance-desktop-records-view">
        {desktopRecordsPanel}
      </div>

      <div className={`maintenance-desktop-table${desktopRecordsPanel ? ' maintenance-desktop-table-hidden' : ''}`}>
        <Table
          rowKey="id"
          dataSource={forklifts}
          columns={columns}
          pagination={false}
          rowClassName={(record) => `${rowClassName(record)} forklift-row-${record.id}`}
          size="middle"
          expandable={{ expandedRowRender, expandedRowKeys: selectedForkliftId ? [selectedForkliftId] : [], expandIcon: () => null, showExpandColumn: false }}
          onRow={(record) => ({ onClick: () => onViewRecords(record) })}
          scroll={{ x: 'max-content' }}
        />
      </div>

      <div className="maintenance-mobile-list">
        {selectedForkliftId && desktopRecordsPanel ? (
          <div className="maintenance-mobile-records-view">
            {desktopRecordsPanel}
          </div>
        ) : forklifts.length === 0 ? (
          <div className="maint-empty">No forklifts found.</div>
        ) : (
          forklifts.map((forklift) => (
            <ForkliftMobileCard
              key={forklift.id}
              forklift={forklift}
              selected={forklift.id === selectedForkliftId}
              onSelect={onSelectForklift}
              onViewRecords={onViewRecords}
            />
          ))
        )}
      </div>
    </section>
  );
}
